import {
  intranetAcknowledgementsPath,
  intranetNewsPath,
  PermissionCode,
  type IntranetFeedItem,
  type IntranetHighlightSpan,
  type PinIntranetContentRequest,
} from '@gb-ticket/shared-types';
import { MoreVerticalIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import { IntranetHighlightDialog } from '@/products/intranet/components/intranet-highlight-dialog';
import {
  calendarMenuEntries,
  copyIntranetArticleUrl,
  intranetContentMenuActions,
  type CalendarMenuEntry,
} from '@/products/intranet/features/intranet/intranet-content-menu-actions';
import {
  useDeleteIntranetContent,
  useSetIntranetPinned,
  useSetIntranetPublished,
} from '@/products/intranet/features/intranet/use-intranet';
import { intranetEditPath } from '@/products/intranet/intranet-paths';
import { cn } from '@/lib/utils';

const CALENDAR_MENU_LABELS: Record<CalendarMenuEntry, string> = {
  edit: 'Bearbeiten',
  copy: 'Link kopieren',
  open: 'Gehe zum Beitrag',
  publish: 'Veröffentlichen',
  unpublish: 'Zurückziehen',
  'highlight-add': 'Zur Highlight hinzufügen',
  'highlight-change': 'Highlight-Zeit ändern',
  'highlight-clear': 'Aus Highlight nehmen',
  acknowledgements: 'Bestätigungen',
  delete: 'Löschen',
};

export function IntranetContentMenu({
  item,
  overlay,
  variant = 'feed',
}: {
  item: IntranetFeedItem;
  overlay?: boolean;
  variant?: 'feed' | 'calendar';
}) {
  const me = useMe();
  const navigate = useNavigate();
  const setPinned = useSetIntranetPinned();
  const setPublished = useSetIntranetPublished();
  const remove = useDeleteIntranetContent();
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const actions = intranetContentMenuActions({
    canEdit: Boolean(item.canEdit),
    canDelete: hasPermission(me.data, PermissionCode.INTRANET_CONTENT_MANAGE),
    canWrite: hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE),
    kind: item.kind,
    highlighted: item.pinned,
  });
  const calendarEntries =
    variant === 'calendar'
      ? calendarMenuEntries({
          canEdit: Boolean(item.canEdit),
          canDelete: hasPermission(me.data, PermissionCode.INTRANET_CONTENT_MANAGE),
          canWrite: hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE),
          canAcknowledge:
            Boolean(item.requiresAcknowledgement) &&
            hasPermission(me.data, PermissionCode.INTRANET_ACKNOWLEDGEMENTS_READ),
          kind: item.kind,
          highlighted: item.pinned,
          isDraft: item.isDraft,
        })
      : [];
  const highlighted = item.pinned;

  async function copyLink() {
    const ok = await copyIntranetArticleUrl(
      `${window.location.origin}${intranetNewsPath(item.slug)}`,
    );
    if (ok) {
      toast.success('Link kopiert.');
      return;
    }
    toast.error('Link konnte nicht kopiert werden.');
  }

  async function saveHighlight(input: PinIntranetContentRequest) {
    try {
      await setPinned.mutateAsync({ slug: item.slug, pinned: true, ...input });
      toast.success(highlighted ? 'Highlight-Zeit gespeichert.' : 'Highlight gesetzt.');
      setHighlightOpen(false);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Es können höchstens 6 Beiträge im Highlight stehen. Bitte zuerst einen lösen.',
      );
    }
  }

  async function clearHighlight() {
    try {
      await setPinned.mutateAsync({ slug: item.slug, pinned: false });
      toast.success('Highlight entfernt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Highlight entfernen fehlgeschlagen.',
      );
    }
  }

  async function setPublishedState(published: boolean) {
    try {
      const result = await setPublished.mutateAsync({ slug: item.slug, published });
      if (!published) {
        toast.success('Zurück in den Entwurf gesetzt.');
      } else if (result.isDraft) {
        toast.success('Veröffentlichung geplant.');
      } else {
        toast.success('Veröffentlicht.');
      }
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Aktion fehlgeschlagen.');
    }
  }

  function runCalendarEntry(entry: CalendarMenuEntry) {
    if (entry === 'edit') {
      void navigate(intranetEditPath(item.slug));
      return;
    }
    if (entry === 'copy') {
      void copyLink();
      return;
    }
    if (entry === 'open') {
      void navigate(intranetNewsPath(item.slug));
      return;
    }
    if (entry === 'publish') {
      void setPublishedState(true);
      return;
    }
    if (entry === 'unpublish') {
      void setPublishedState(false);
      return;
    }
    if (entry === 'highlight-add' || entry === 'highlight-change') {
      setHighlightOpen(true);
      return;
    }
    if (entry === 'highlight-clear') {
      void clearHighlight();
      return;
    }
    if (entry === 'acknowledgements') {
      void navigate(intranetAcknowledgementsPath(item.slug));
      return;
    }
    setPendingDelete(true);
  }

  async function confirmDelete() {
    try {
      await remove.mutateAsync(item.slug);
      toast.success('Beitrag gelöscht.');
      setPendingDelete(false);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.');
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              'size-8 shrink-0',
              overlay && 'bg-black/35 text-white hover:bg-black/50 hover:text-white',
            )}
            aria-label={`Aktionen für ${item.title}`}
          >
            <MoreVerticalIcon className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {variant === 'calendar'
            ? calendarEntries.map((entry) => (
                <DropdownMenuItem
                  key={entry}
                  className={entry === 'delete' ? 'text-destructive' : undefined}
                  onSelect={() => runCalendarEntry(entry)}
                >
                  {CALENDAR_MENU_LABELS[entry]}
                </DropdownMenuItem>
              ))
            : null}
          {variant === 'feed' && actions.edit ? (
            <DropdownMenuItem onSelect={() => navigate(intranetEditPath(item.slug))}>
              Bearbeiten
            </DropdownMenuItem>
          ) : null}
          {variant === 'feed' && actions.remove ? (
            <DropdownMenuItem onSelect={() => setPendingDelete(true)}>Löschen</DropdownMenuItem>
          ) : null}
          {variant === 'feed' ? (
            <DropdownMenuItem onSelect={() => void copyLink()}>Link kopieren</DropdownMenuItem>
          ) : null}
          {variant === 'feed' && actions.highlight === 'add' ? (
            <DropdownMenuItem onSelect={() => setHighlightOpen(true)}>
              Zur Highlight hinzufügen
            </DropdownMenuItem>
          ) : null}
          {variant === 'feed' && actions.highlight === 'change' ? (
            <DropdownMenuItem onSelect={() => setHighlightOpen(true)}>
              Highlight-Zeit ändern
            </DropdownMenuItem>
          ) : null}
          {variant === 'feed' && actions.highlight === 'change' ? (
            <DropdownMenuItem onSelect={() => void clearHighlight()}>
              Aus Highlight nehmen
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <IntranetHighlightDialog
        open={highlightOpen}
        title={highlighted ? 'Highlight-Zeit ändern' : 'Zum Highlight hinzufügen'}
        description={item.title}
        initialSpan={(item.highlightSpan as IntranetHighlightSpan | null) ?? null}
        initialUntil={item.highlightUntil ?? null}
        pending={setPinned.isPending}
        onCancel={() => setHighlightOpen(false)}
        onSave={(input) => void saveHighlight(input)}
      />
      <ConfirmDeleteDialog
        open={pendingDelete}
        title="Beitrag endgültig löschen?"
        description={`„${item.title}“ wird mit allen Kommentaren entfernt.`}
        pending={remove.isPending}
        onCancel={() => setPendingDelete(false)}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
}
